import { Item } from "@/components/ui/item";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  FileIcon, FileTextIcon, FileVideoIcon, FileAudioIcon, ImageIcon,
  FileArchiveIcon, Download, Eye, Loader2, XIcon, type LucideIcon,
} from "lucide-react";

export interface FilePillProps {
  filename: string;
  mediaType?: string | null;
  thumbnailUrl?: string;
  onOpen?: () => void;
  onRemove?: () => void;
  removing?: boolean;
  opening?: boolean;
  size?: "sm" | "md";
  ariaLabel?: string;
  removeAriaLabel?: string;
  className?: string;
  /** Optional action hint; existing consumers retain their current appearance. */
  openAction?: "view" | "download";
}

export function iconForFile(mediaType: string | null | undefined, filename: string | undefined): LucideIcon {
  const mt = (mediaType ?? "").toLowerCase();
  const ext = filename?.split(".").pop()?.toLowerCase() ?? "";
  if (mt.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "avif", "bmp", "ico", "svg"].includes(ext)) return ImageIcon;
  if (mt.startsWith("audio/") || ["mp3", "wav", "m4a", "aac", "ogg", "flac", "opus"].includes(ext)) return FileAudioIcon;
  if (mt.startsWith("video/") || ["mp4", "webm", "mov", "avi", "mkv"].includes(ext)) return FileVideoIcon;
  if (mt === "application/pdf" || ext === "pdf") return FileTextIcon;
  if (["zip", "tar", "gz", "tgz", "bz2", "xz", "7z"].includes(ext)) return FileArchiveIcon;
  return FileIcon;
}

export function FilePill({ filename, mediaType, thumbnailUrl, onOpen, onRemove, removing = false,
  opening = false, size = "sm", ariaLabel, removeAriaLabel, className, openAction }: FilePillProps) {
  const { t } = useTranslation();
  const Icon = iconForFile(mediaType, filename);
  const isImage = (mediaType ?? "").startsWith("image/");
  const actionLabel = openAction
    ? t(openAction === "view" ? "fileBrowser.viewFile" : "fileBrowser.downloadFile", { name: filename })
    : undefined;
  const ActionIcon = openAction === "view" ? Eye : Download;
  return (
    <Item variant="outline" size="sm" className={cn(
      "max-w-[220px] flex-nowrap gap-0 bg-muted/50 p-0 transition-colors hover:bg-muted",
      className,
    )} data-testid="file-pill" data-filename={filename}>
      <button type="button" onClick={onOpen} disabled={!onOpen || removing || opening}
        aria-label={ariaLabel ?? actionLabel ?? filename} title={actionLabel} className={cn(
          "flex min-w-0 items-center gap-2 rounded-l-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          size === "md" ? "px-2.5 py-2" : "px-2 py-1.5",
          onOpen && "cursor-pointer",
        )}>
        {opening ? <Loader2 className="size-4 shrink-0 animate-spin" /> : isImage && thumbnailUrl ? (
          <img src={thumbnailUrl} alt="" className="size-8 shrink-0 rounded object-cover" loading="lazy" />
        ) : <Icon className="size-4 shrink-0 text-muted-foreground" />}
        <span className="truncate text-xs font-medium" title={filename}>{filename}</span>
        {openAction && <ActionIcon aria-hidden="true" className="ml-auto size-3 shrink-0 text-muted-foreground/60" />}
      </button>
      {onRemove && <button type="button" onClick={onRemove} disabled={removing || opening}
        aria-label={removeAriaLabel ?? t("attachment.remove", { filename })}
        className="mr-1 rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="remove-attachment">
        {removing ? <Loader2 className="size-3.5 animate-spin" /> : <XIcon className="size-3.5" />}
      </button>}
    </Item>
  );
}
